"use client";

import React, { useEffect, useState } from "react";
import { useCart } from "@/src/contexts/CartContext";
import { useTenant } from "@/src/contexts/TenantContext";
import type { IProduct } from "@/src/types/product";
import { SafeImage } from "@/src/components/SafeImage";
import { VariantSelector } from "@/src/app/shop/product/[id]/components/VariantSelector";
import { VariantAxesSelector, type VariantAxis, type V3Variant } from "@/src/app/shop/product/[id]/components/VariantAxesSelector";
import { CountdownTimer } from "./CountdownTimer";
import { isOfferActive } from "@/src/utils/products/mapProducts";

interface Props {
  product: IProduct;
  onClose: () => void;
}

/**
 * Right-side drawer for picking a product variant straight from the shop grid.
 * Shows the product image, the option pills, and live price (selling / MRP /
 * discount). Variants are preloaded on the product (from the landing payload),
 * so the drawer opens instantly with no fetch.
 */
export function VariantPickerModal({ product, onClose }: Props) {
  const { addItem } = useCart();
  const { tenantId } = useTenant();
  const variants = product.variants ?? [];
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  // Structured variant axes (same as the product detail page) from the v3 API.
  const [axes, setAxes] = useState<VariantAxis[]>([]);
  const [v3Variants, setV3Variants] = useState<V3Variant[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL;
        if (!base || !product.id) return;
        const res = await fetch(`${base}/v3/products/${product.id}`);
        if (!res.ok) return;
        const json = await res.json();
        const d = json?.data;
        if (!cancelled && d) {
          setAxes(Array.isArray(d.variantAxes) ? d.variantAxes : []);
          setV3Variants(Array.isArray(d.variants) ? d.variants : []);
        }
      } catch {
        /* fall back to the flat variant selector */
      }
    })();
    return () => { cancelled = true; };
  }, [product.id]);
  // Photo of the colour picked in the axes selector — set before a full
  // variant resolves, so the hero swaps the moment a colour is tapped.
  const [axisImage, setAxisImage] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [shown, setShown] = useState(false);

  // Slide in on mount.
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const close = () => {
    setShown(false);
    window.setTimeout(onClose, 220); // let the slide-out animation finish
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedVariant = variants.find((v) => v.variantId === selectedVariantId) ?? null;
  // Swap to the selected variant's own photo when it has one (e.g. colour variants).
  const displayImage = axisImage || selectedVariant?.thumbnail || product.image;
  const hasImage = !!displayImage && displayImage.startsWith("http");
  const lowStock =
    !!selectedVariant &&
    (selectedVariant.stockQuantity ?? 0) > 0 &&
    (selectedVariant.stockQuantity ?? 0) <= 10;
  const dealActive = isOfferActive(product.offerStartTime, product.offerEndTime);

  // Price: base product until a variant is picked, then the variant's own price.
  const selling = selectedVariant
    ? parseFloat(selectedVariant.sellingPrice || "0") || product.price
    : product.price;
  const mrp = selectedVariant
    ? parseFloat(selectedVariant.mrp || "0") || product.originalPrice || 0
    : product.originalPrice || 0;
  const discountPct = mrp > selling && mrp > 0 ? Math.round(((mrp - selling) / mrp) * 100) : 0;
  const canAdd = !!selectedVariant && (selectedVariant.stockQuantity ?? 0) > 0;

  const handleAdd = async () => {
    if (!selectedVariant) return;
    const tid = product.tenantId || tenantId;
    if (!tid) {
      setError("Missing store info");
      return;
    }
    setAdding(true);
    try {
      await addItem({
        productId: product.id,
        outletId: product.outletId,
        tenantId: tid,
        variantId: selectedVariant.variantId,
        name: `${product.name} (${selectedVariant.label})`,
        price: selling,
        mrp: mrp || undefined,
        quantity: 1,
        image: product.image,
      });
      close();
    } catch (e) {
      setError((e as Error)?.message || "Failed to add to cart");
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] grid place-items-center p-4" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 transition-opacity duration-200"
        style={{ background: "rgba(15,12,8,0.5)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)", opacity: shown ? 1 : 0 }}
        onClick={close}
      />

      {/* Centered modal */}
      <div
        className="relative z-10 flex w-full max-w-[760px] flex-col overflow-hidden bg-white sm:flex-row"
        style={{
          maxHeight: "88vh",
          borderRadius: 24,
          boxShadow: "0 30px 80px -20px rgba(20,15,5,0.45)",
          transform: shown ? "scale(1)" : "scale(0.96)",
          opacity: shown ? 1 : 0,
          transition: "transform 220ms cubic-bezier(.22,.61,.36,1), opacity 200ms ease",
        }}
      >
        {/* Close */}
        <button
          onClick={close}
          aria-label="Close"
          className="absolute right-3.5 top-3.5 z-20 flex h-9 w-9 items-center justify-center rounded-full transition hover:scale-105"
          style={{ background: "rgba(255,255,255,0.92)", color: "#5C5953", boxShadow: "0 2px 10px rgba(20,15,5,0.14)" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Left — image */}
        <div
          className="relative h-[260px] shrink-0 sm:h-auto sm:w-[44%]"
          style={{ background: "linear-gradient(135deg,#F4F1EA 0%,#ECE7DC 100%)" }}
        >
          {hasImage ? (
            <SafeImage src={displayImage} alt={product.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <span style={{ fontFamily: "var(--font-display)", fontSize: 64, fontWeight: 700, color: "rgba(9,42,76,0.14)" }}>
                {product.name.slice(0, 1).toUpperCase()}
              </span>
            </div>
          )}
          {discountPct >= 5 && (
            <span
              className="absolute left-4 top-4 rounded-full px-3 py-1 text-[11px] font-bold text-white"
              style={{ background: "#F26250" }}
            >
              {discountPct}% OFF
            </span>
          )}
        </div>

        {/* Right — details + actions */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-6 sm:p-7">
            <h3
              className="pr-8 leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(20px,2.4vw,25px)", letterSpacing: "-0.01em", color: "#1D1D1D" }}
            >
              {product.name}
            </h3>

            {/* Price */}
            <div className="mt-3 flex flex-wrap items-baseline gap-2">
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 26, color: "#1D1D1D" }}>
                ₹{selling.toLocaleString("en-IN")}
              </span>
              {mrp > selling && (
                <span className="text-[14px] line-through" style={{ color: "#938E84" }}>
                  ₹{mrp.toLocaleString("en-IN")}
                </span>
              )}
              {discountPct >= 5 && (
                <span className="rounded-md px-2 py-0.5 text-[12px] font-bold" style={{ color: "#D94A39", background: "#FBE4DF" }}>
                  {discountPct}% off
                </span>
              )}
            </div>

            {/* Limited time deal + countdown */}
            {dealActive && product.offerEndTime && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: "#FEF0EC", color: "#D94A39" }}>
                  Limited time deal
                </span>
                <CountdownTimer mini endsAt={product.offerEndTime} />
              </div>
            )}

            {/* Options */}
            <div className="mt-6">
              {variants.length === 0 ? (
                <p className="text-[13px]" style={{ color: "#938E84" }}>No options available.</p>
              ) : (
                <>
                  {axes.length > 0 && v3Variants.length > 0 ? (
                    <VariantAxesSelector
                      axes={axes}
                      variants={v3Variants}
                      selectedVariantId={selectedVariantId}
                      onSelect={setSelectedVariantId}
                      onImageChange={setAxisImage}
                    />
                  ) : (
                    <VariantSelector
                      variants={variants}
                      selectedVariantId={selectedVariantId}
                      onSelect={setSelectedVariantId}
                    />
                  )}
                  {lowStock && (
                    <p className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: "#F26250" }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#F26250", display: "inline-block", animation: "blip 1s ease-in-out infinite" }} />
                      Only {selectedVariant?.stockQuantity} left
                    </p>
                  )}
                  {error && <p className="mt-3 text-[12px]" style={{ color: "#F26250" }}>{error}</p>}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-5 sm:px-7" style={{ borderColor: "#F0EDE6" }}>
            <button
              onClick={handleAdd}
              disabled={!canAdd || adding}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full text-[15px] font-bold text-white transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: "#F26250", boxShadow: canAdd ? "0 10px 26px -10px rgba(242,98,80,0.7)" : "none" }}
            >
              {adding ? "Adding…" : !selectedVariant ? "Select an option" : canAdd ? "Add to cart" : "Out of stock"}
              {canAdd && !adding && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
